import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "services/api";
import { getCurrentUser, requireAuth } from "actions/AuthAction";
import { isAdminRole } from "utils/roles";
import PengajuanRiwayatDialog from "../components/PengajuanRiwayatDialog";
import PengajuanFilterBar from "../components/PengajuanFilterBar";

const statusLabel = {
  DRAFT: "Draft",
  DIAJUKAN: "Diajukan",
  VERIFIKASI: "Diverifikasi",
  PERBAIKAN: "Perlu Perbaikan",
  PROSES_TEKNIS: "Proses Teknis",
  DISETUJUI: "Disetujui",
  DITOLAK: "Ditolak",
  PENANDATANGANAN: "Penandatanganan",
  SELESAI: "Selesai",
};

const statusColor = {
  DRAFT: "default",
  DIAJUKAN: "info",
  VERIFIKASI: "warning",
  PERBAIKAN: "error",
  PROSES_TEKNIS: "warning",
  DISETUJUI: "success",
  DITOLAK: "error",
  PENANDATANGANAN: "primary",
  SELESAI: "success",
};

const ALL_STATUS_OPTIONS = [
  { value: "", label: "Semua Status" },
  ...Object.entries(statusLabel).map(([value, label]) => ({ value, label })),
];

const QUEUE_STATUS_OPTIONS = [
  { value: "", label: "Semua Antrian" },
  { value: "DIAJUKAN", label: "Diajukan" },
  { value: "VERIFIKASI", label: "Diverifikasi" },
  { value: "PERBAIKAN", label: "Perlu Perbaikan" },
  { value: "PROSES_TEKNIS", label: "Proses Teknis" },
];

const defaultFilters = () => ({
  tahun: "",
  bulan: "",
  status: "",
});

function buildQueryParams(filters) {
  const params = new URLSearchParams();
  if (filters.tahun) params.set("tahun", filters.tahun);
  if (filters.bulan) params.set("bulan", filters.bulan);
  if (filters.status) params.set("status", filters.status);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function DaftarPengajuan() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [riwayatOpen, setRiwayatOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const user = getCurrentUser();
  const isPemohon = user?.role === "PEMOHON";
  const isAdminView = isAdminRole(user?.role);

  const loadData = useCallback(() => {
    setLoading(true);
    const endpoint = isPemohon || isAdminView ? "pengajuan" : "verifikasi/pengajuan";
    api
      .get(`${endpoint}${buildQueryParams(filters)}`)
      .then((res) => setRows(res.data?.data || []))
      .catch(() => alert("Gagal memuat pengajuan"))
      .finally(() => setLoading(false));
  }, [filters, isPemohon, isAdminView]);

  useEffect(() => {
    const allowed = isPemohon
      ? ["PEMOHON"]
      : ["PETUGAS", "VERIFIKATOR", "PIMPINAN", "ADMIN", "SUPER_ADMIN"];
    const u = requireAuth(navigate, allowed);
    if (!u) return;
    loadData();
  }, [navigate, isPemohon, loadData]);

  const handleRowClick = (row) => {
    if (!row?.pengajuanId) return;
    setSelectedId(row.pengajuanId);
    setRiwayatOpen(true);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters());
  };

  const columns = [
    { Header: "Nomor", accessor: "nomor", width: "16%" },
    ...(isPemohon ? [] : [{ Header: "Pemohon", accessor: "pemohon", width: "18%" }]),
    { Header: "Layanan", accessor: "layanan", width: "28%" },
    { Header: "Status", accessor: "status", width: "14%" },
    { Header: "Tanggal", accessor: "tanggal", width: "14%" },
    { Header: "Aksi", accessor: "action", align: "center" },
  ];

  const tableRows = rows.map((p) => ({
    pengajuanId: p.id,
    nomor: p.nomor_pengajuan,
    pemohon: p.nama_pemohon,
    layanan: p.nama_jenis_izin,
    status: (
      <Chip label={statusLabel[p.status] || p.status} size="small" color={statusColor[p.status] || "default"} />
    ),
    tanggal: p.tanggal_pengajuan
      ? new Date(p.tanggal_pengajuan).toLocaleDateString("id-ID")
      : new Date(p.created_at).toLocaleDateString("id-ID"),
    action: (
      <MDButton
        size="small"
        color="info"
        variant="text"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/pengajuan/${p.id}`);
        }}
      >
        Detail
      </MDButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="medium">
              {isPemohon ? "Pengajuan Saya" : isAdminView ? "Semua Pengajuan" : "Daftar Pengajuan"}
            </MDTypography>
            <MDTypography variant="button" color="text">
              Filter berdasarkan tahun, bulan, dan status • klik baris untuk riwayat
            </MDTypography>
          </MDBox>
          {isPemohon && (
            <MDButton variant="gradient" color="info" onClick={() => navigate("/layanan")}>
              Ajukan Baru
            </MDButton>
          )}
        </MDBox>

        <Card>
          <MDBox p={3}>
            <PengajuanFilterBar
              filters={filters}
              onChange={setFilters}
              onReset={handleResetFilters}
              statusOptions={isAdminView || isPemohon ? ALL_STATUS_OPTIONS : QUEUE_STATUS_OPTIONS}
            />

            {loading ? (
              <MDTypography>Memuat...</MDTypography>
            ) : rows.length === 0 ? (
              <MDTypography color="text">Tidak ada data pengajuan untuk filter ini.</MDTypography>
            ) : (
              <DataTable
                table={{ columns, rows: tableRows }}
                canSearch
                entriesPerPage={{ defaultValue: 10 }}
                onRowClick={handleRowClick}
              />
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />

      <PengajuanRiwayatDialog
        open={riwayatOpen}
        pengajuanId={selectedId}
        onClose={() => {
          setRiwayatOpen(false);
          setSelectedId(null);
        }}
        onOpenDetail={(id) => {
          setRiwayatOpen(false);
          navigate(`/pengajuan/${id}`);
        }}
      />
    </DashboardLayout>
  );
}

export default DaftarPengajuan;
